// Period math for the analytics dashboards, ported from the AnalyticsServer debug client.
// All math is in UTC; the server treats the dates as account-local days (no timezone sent).

import i18n from "../i18n";

// Period labels follow the UI language (browser locale would show English months on a Hebrew UI).
// Guard against a non-standard i18n.language value (e.g. a numeric code) that would make
// toLocaleDateString throw "Invalid language tag"; fall back to English (mirrors AnalyticsWidget).
const lang = () => {
  const l = i18n && i18n.language;
  try { if (l) { Intl.getCanonicalLocales(l); return l; } } catch (e) { /* invalid BCP-47 tag */ }
  return "en";
};

export function ymd(d) { return d.toISOString().slice(0, 10); }
export function parseYmd(s) { return new Date(s + "T00:00:00Z"); }
export function addDays(d, n) { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; }
export function addMonths(d, n) { const x = new Date(d); x.setUTCMonth(x.getUTCMonth() + n); return x; }
export function startOfMonth(d) { return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)); }
// Week start (Sunday vs Monday) depends on the account country; injected by the analytics page to keep
// this util store-free (mirrors the currency-symbol injection). Default Sunday (the platform is IL-centric).
let _weekStartsSunday = true;
export function setAnalyticsWeekStart(startsSunday) { _weekStartsSunday = !!startsSunday; }
export function weekStartsSunday() { return _weekStartsSunday; }

// Countries whose week starts Monday (ISO-3166 alpha-2); every other country starts Sunday (the chosen
// default). Covers Europe + ex-USSR — includes the app's non-Sunday accounts (RU/UA/RS/ME/RO/KZ).
const MONDAY_START_COUNTRIES = new Set([
  "AD", "AL", "AT", "AX", "BA", "BE", "BG", "BY", "CH", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FO",
  "FR", "GB", "GG", "GI", "GL", "GR", "HR", "HU", "IE", "IM", "IS", "IT", "JE", "KZ", "LI", "LT", "LU",
  "LV", "MC", "MD", "ME", "MK", "MT", "NL", "NO", "PL", "PT", "RO", "RS", "RU", "SE", "SI", "SJ", "SK",
  "SM", "UA", "VA", "XK"
]);
export function weekStartsSundayFor(countryCode) {
  return !MONDAY_START_COUNTRIES.has(String(countryCode || "").toUpperCase());
}

// Start of the week containing d, honoring the account's week-start (Sunday or Monday).
export function startOfWeek(d) {
  const dow = d.getUTCDay();                          // 0=Sun … 6=Sat
  const back = _weekStartsSunday ? dow : ((dow + 6) % 7);
  return addDays(d, -back);
}

// Day-first date tokens per the 23/07 spec (Hebrew writing taken as the model for every
// language): "29 Dec 2025" / "29 дек 2025" / "29 בדצמ׳ 2025". The month token keeps a locale
// particle glued to the month by the locale's own pattern (Hebrew ב) and drops abbreviation
// dots and the Russian "г." suffix (those come from composing the label ourselves).
function dateTokens(date, loc) {
  const parts = new Intl.DateTimeFormat(loc, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).formatToParts(date);
  let d = "", m = "", y = "";
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p.type === "day") d = p.value;
    else if (p.type === "year") y = p.value;
    else if (p.type === "month") {
      // a non-space literal right before the month (Hebrew " ב") belongs to the month token
      const before = i > 0 && parts[i - 1].type === "literal" ? parts[i - 1].value.replace(/[\s,.]+/g, "") : "";
      m = before + p.value.replace(/\./g, "");
    }
  }
  return { d, m, y };
}

// Label builders fall back to English if the locale data itself misbehaves (mirrors lang()).
function buildLabel(make) {
  try { return make(lang()); } catch (e) { return make("en"); }
}

export function dayLabel(date) {
  return buildLabel(loc => { const t = dateTokens(date, loc); return `${t.d} ${t.m} ${t.y}`; });
}

// Month pill formats WITHOUT a day — reusing dateTokens would drag in the day-pattern particle
// (Hebrew "ביולי 2026", "in July"); the standalone month-year pattern gives the plain "יולי 2026".
function monthYearLabel(date) {
  return buildLabel(loc => {
    const parts = new Intl.DateTimeFormat(loc, { month: "short", year: "numeric", timeZone: "UTC" }).formatToParts(date);
    const get = type => ((parts.find(p => p.type === type) || {}).value || "");
    return `${get("month").replace(/\./g, "")} ${get("year")}`;
  });
}

// Range label per the 23/07 spec: day BEFORE month, short month, year always — shared parts collapsed:
//   same month          → "4 – 10 Jan 2026"
//   same year           → "29 Mar – 4 Apr 2026"
//   across a year edge  → "28 Dec 2025 – 3 Jan 2026"
function weekRangeLabel(from, to) {
  return buildLabel(loc => {
    const a = dateTokens(from, loc), b = dateTokens(to, loc);
    if (from.getUTCFullYear() !== to.getUTCFullYear()) return `${a.d} ${a.m} ${a.y} – ${b.d} ${b.m} ${b.y}`;
    if (from.getUTCMonth() !== to.getUTCMonth()) return `${a.d} ${a.m} – ${b.d} ${b.m} ${b.y}`;
    return `${a.d} – ${b.d} ${b.m} ${b.y}`;
  });
}

export function todayAnchor() {
  const n = new Date();
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
}

// Picked (inclusive) date -> exclusive end (date + 1 day). Invalid input passes through untouched.
export function exclusiveEnd(value) {
  const ms = Date.parse(value + "T00:00:00Z");
  if (isNaN(ms)) return value;
  return new Date(ms + 86400000).toISOString().slice(0, 10);
}

// Half-open [from, to) range for a granularity + anchor, with a display label.
export function periodRange(granularity, anchor) {
  if (granularity === "day") {
    return {
      from: ymd(anchor), to: ymd(addDays(anchor, 1)),
      label: dayLabel(anchor)
    };
  }
  if (granularity === "month") {
    const from = startOfMonth(anchor);
    return {
      from: ymd(from), to: ymd(addMonths(from, 1)),
      // short month per the design ("Jul 2026" — the long name overflowed the pill); composed from
      // tokens so Russian comes out "июл 2026", not "июл. 2026 г."
      label: monthYearLabel(from)
    };
  }
  const from = startOfWeek(anchor);
  return { from: ymd(from), to: ymd(addDays(from, 7)), label: weekRangeLabel(from, addDays(from, 6)) };
}

const isValidYmd = s => !isNaN(Date.parse(s + "T00:00:00Z"));

// Active range sent to the API for a granularity/anchor, or custom from/to (inclusive end).
// A cleared date input yields "" — the Invalid Date would crash later in toISOString/Intl
// (RangeError: Invalid time value), so invalid custom bounds fall back to the last 30 days.
export function activeRange(granularity, anchor, customFrom, customTo) {
  if (granularity === "custom") {
    const from = isValidYmd(customFrom) ? customFrom : ymd(addDays(todayAnchor(), -30));
    const to = isValidYmd(customTo) ? exclusiveEnd(customTo) : ymd(addDays(todayAnchor(), 1));
    return { from, to };
  }
  const r = periodRange(granularity, anchor);
  return { from: r.from, to: r.to };
}

export function compareLabel(mode) {
  return mode === "previous" ? "previous period"
    : mode === "last_month" ? "same period last month"
      : "same period last year";
}

// Comparison range for the selected mode, or null.
// For the CURRENT (in-progress) period we compare against the same elapsed slice of the prior period
// (period-to-date), so an incomplete month/week/day isn't dragged negative versus a full prior one —
// e.g. on Jul 12, "this month vs previous" compares Jul 1–12 against Jun 1–12, not full June.
export function comparisonRange(mode, granularity, anchor, customFrom, customTo) {
  if (!mode || mode === "none") return null;
  const today = todayAnchor();

  if (granularity === "custom") {
    const base = activeRange("custom", anchor, customFrom, customTo);
    const from = parseYmd(base.from);
    let toExcl = parseYmd(base.to);
    const tomorrow = addDays(today, 1);
    if (toExcl > tomorrow) toExcl = tomorrow;            // never compare against days not yet elapsed
    let f, t;
    if (mode === "previous") { const days = Math.max(1, Math.round((toExcl - from) / 86400000)); f = addDays(from, -days); t = addDays(toExcl, -days); }
    else { f = addMonths(from, -1); t = addMonths(toExcl, -1); }   // same_slot: one cycle (month) earlier
    return { from: ymd(f), to: ymd(t), label: compareLabel(mode) };
  }

  const periodStart = granularity === "month" ? startOfMonth(anchor)
    : granularity === "week" ? startOfWeek(anchor)
      : anchor;                                          // day
  const nextStart = granularity === "month" ? addMonths(periodStart, 1)
    : granularity === "week" ? addDays(periodStart, 7)
      : addDays(periodStart, 1);

  // In-progress period = the one containing today; only [periodStart, today+1) has elapsed.
  const curStart = granularity === "month" ? startOfMonth(today)
    : granularity === "week" ? startOfWeek(today)
      : today;
  const inProgress = ymd(periodStart) === ymd(curStart);
  const elapsedEnd = inProgress && addDays(today, 1) < nextStart ? addDays(today, 1) : nextStart;
  const sliceDays = Math.max(1, Math.round((elapsedEnd - periodStart) / 86400000));

  // Start of the period we compare against.
  let prevStart;
  if (mode === "previous") {
    prevStart = granularity === "month" ? addMonths(periodStart, -1)
      : granularity === "week" ? addDays(periodStart, -7)
        : addDays(periodStart, -1);
  } else {                                               // same_slot: same slot one cycle up
    prevStart = granularity === "day" ? addDays(periodStart, -7)      // same weekday last week
      : granularity === "week" ? addMonths(periodStart, -1)          // same week last month (approx)
        : addMonths(periodStart, -12);                                // same month last year
  }

  const prevNext = granularity === "month" ? addMonths(prevStart, 1)
    : granularity === "week" ? addDays(prevStart, 7)
      : addDays(prevStart, 1);
  // In-progress period → same elapsed slice (capped at the prior period's end for shorter months);
  // a fully-elapsed past period → the whole prior period (unchanged full-vs-full behaviour).
  let prevEnd = inProgress ? addDays(prevStart, sliceDays) : prevNext;
  if (prevEnd > prevNext) prevEnd = prevNext;
  const result = { from: ymd(prevStart), to: ymd(prevEnd), label: compareLabel(mode) };
  // When the elapsed slice is a single (partial) day — a day period, or the first day of a week/month —
  // cut the prior day at the current hour so it isn't a full 24h vs today's few hours. (For a multi-day
  // slice only the last day is partial; a single hour filter would wrongly cap the full earlier days too,
  // so we leave those whole.) Hour is browser-local — correct when the viewer shares the account tz.
  if (inProgress && sliceDays === 1) result.hourCeiling = new Date().getHours();
  return result;
}

// Shift the anchor one period in the given direction.
export function navAnchor(granularity, anchor, dir) {
  if (granularity === "day") return addDays(anchor, dir);
  if (granularity === "month") return addMonths(anchor, dir);
  return addDays(anchor, 7 * dir); // week
}

// Currency symbol provider, injected by the analytics page from portal data (country-based).
// Kept as an injection point so this util stays store-free.
let _currencySymbol = null;
export function setAnalyticsCurrencySymbol(symbol) { _currencySymbol = symbol || null; }

// Display-format a value by the metric's format hint (mirrors debug.html).
// opts.integer: round currency to whole units.
// opts.decimals: how many decimals a rate keeps, PADDED to that many — an average shown as "3.7"
//   next to one shown as "3.72" reads like different precision, and Fullgaz asked for the pair of
//   decimals everywhere. It also drives the sub-hour duration form, so a KPI row is uniform.
export const EMPTY_VALUE = "—";

export function formatValue(value, format, opts) {
  if (value == null) return EMPTY_VALUE;
  // An aggregate over zero rows (avg/min/max with no delivered orders) arrives as NaN/Inf —
  // that's "no data", not a number to print.
  const raw = typeof value === "number" ? value : String(value).trim().toLowerCase();
  if ((typeof raw === "number" && isNaN(raw)) || raw === "nan" || raw === "inf" || raw === "-inf" || raw === "infinity" || raw === "-infinity") return EMPTY_VALUE;
  if (format === "minute_of_day") {
    const n = Number(value);
    if (isNaN(n)) return String(value);
    const h = Math.floor(n / 60), m = Math.round(n % 60);
    return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
  }
  // Sub-hour durations keep decimals when the caller asks for them (the KPI cards do): a whole
  // "29m" hides the very movement the card exists to show — two runs a quarter-minute apart both
  // read 29m. An hour or more keeps the h+m form, where hundredths of a minute would be noise.
  const preciseMinutes = opts && opts.decimals != null
    ? n => n.toFixed(opts.decimals) + "m"
    : null;
  if (format === "duration_minutes") {
    const exact = Number(value);
    if (isNaN(exact)) return String(value);
    if (exact <= 0) return EMPTY_VALUE;
    if (preciseMinutes && exact < 60) return preciseMinutes(exact);
    const n = Math.round(exact);
    const h = Math.floor(n / 60), m = n % 60;
    return h >= 1 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
  }
  if (format === "duration_seconds") {
    const s = Number(value);
    if (isNaN(s)) return String(value);
    if (s <= 0) return EMPTY_VALUE;
    if (preciseMinutes && s < 3600) return preciseMinutes(s / 60);
    const total = Math.round(s / 60);          // seconds → whole minutes
    const h = Math.floor(total / 60), m = total % 60;
    if (h >= 1) return `${h}h ${String(m).padStart(2, "0")}m`;   // X hr Y min
    return total >= 1 ? `${total}m` : "<1m";                      // Y min (or <1m)
  }
  if (format === "percent") return (value * 100).toFixed(1) + "%";
  if (format === "currency") {
    const digits = opts && opts.integer ? 0 : 2;
    const num = Number(value).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    return _currencySymbol ? _currencySymbol + num : num;
  }
  if (format === "integer") return Number(value).toLocaleString();
  if (format === "number") {
    // Rate-style numbers (deliveries per hour, …): hundredths. An explicit opts.decimals also PADS
    // to that width, so a card never shows "3.7" where its neighbour shows "3.72".
    if (opts && opts.decimals != null)
      return Number(value).toLocaleString(undefined,
        { minimumFractionDigits: opts.decimals, maximumFractionDigits: opts.decimals });
    return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return typeof value === "number" ? value.toLocaleString() : String(value);
}

// Build the /analytics/query request body for a dashboard widget. `scopeFilters` are the dashboard's
// global filter-control selections (e.g. business), merged on top of the widget's own fixed filters.
export function widgetRequest(widget, range, dateBasis, scopeFilters, cleanBoundSeconds, fullRefundShare) {
  const request = { metrics: widget.metrics, dateRange: range };
  if (widget.dimensions && widget.dimensions.length) request.dimensions = widget.dimensions;
  if (widget.timeGrain) request.timeGrain = widget.timeGrain;
  // Week bucketing must match the account's week-start (Sunday/Monday) so server buckets line up
  // with the client's week ranges. Only relevant for the "week" grain.
  if (widget.timeGrain === "week") request.weekStart = weekStartsSunday() ? "sunday" : "monday";
  if (widget.limit) request.limit = widget.limit;
  if (widget.sortBy) request.sort = { by: widget.sortBy, direction: widget.sortDirection || "desc" };
  const basis = dateBasis || widget.dateBasis;
  if (basis) request.dateBasis = basis;
  // The longest delivery the outlier-cleaned time metrics still count. Comes from the dashboard's
  // thresholds (a user override included), so the same number the Settings panel shows is the number
  // the query uses — and every scope, from one control centre to a whole chain, cleans identically.
  if (cleanBoundSeconds > 0) request.cleanBoundSeconds = Math.round(cleanBoundSeconds);
  // What a refund in full is — a share of the order total, from the dashboard's fullRefundPct the
  // same way. Without it the server counts from 90%.
  if (fullRefundShare > 0) request.fullRefundShare = fullRefundShare;
  const own = (widget.filters || []).map(f => ({ dimension: f.dimension, values: f.values }));
  const scope = (scopeFilters || []).filter(f => f && f.dimension && f.values && f.values.length)
    .map(f => ({ dimension: f.dimension, values: f.values }));
  const filters = own.concat(scope);
  if (filters.length) request.filters = filters;
  return request;
}

// A widget may declare a `dayVariant` used when the dashboard period granularity is "day"
// (e.g. a by-day time series becomes a by-hour breakdown for a single day). For any other
// granularity, or when no variant is declared, the base widget is returned unchanged. The
// returned object keeps the widget id, so layout/keys/comparison are unaffected.
export function effectiveWidget(widget, granularity) {
  const v = widget && widget.dayVariant;
  if (granularity !== "day" || !v) return widget;
  // NOTE: the server may serialize an absent variant array as [] (AutoMapper turns a null source
  // collection into an empty one). A widget always needs metrics, so only override them when the
  // variant supplies a non-empty list; an empty/absent list keeps the base metrics. Dimensions are
  // different: an explicit empty list legitimately drops the time axis (e.g. the leads bar variant).
  const hasArr = a => Array.isArray(a) && a.length > 0;
  return {
    ...widget,
    title: v.title || widget.title,
    type: v.type || widget.type,                 // e.g. line_chart -> bar_chart for a single day
    metrics: hasArr(v.metrics) ? v.metrics : widget.metrics,
    dimensions: Array.isArray(v.dimensions) ? v.dimensions : widget.dimensions,  // [] drops dimensions
    timeGrain: v.timeGrain,  // absent in the variant clears the grain (hour-of-day is categorical)
    sortBy: v.sortBy != null ? v.sortBy : widget.sortBy,
    sortDirection: v.sortDirection != null ? v.sortDirection : widget.sortDirection,
  };
}
